import {
  Accordion,
  Badge,
  Box,
  Code,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import useRetrieveRunStepDebugPreview from '@/hooks/api/runs/useRetrieveRunStepDebugPreview.ts';
import DatasetPill from '@/pages/Run/components/DatasetPill.tsx';
import RunInlineEmptyState from '@/pages/Run/components/RunInlineEmptyState.tsx';
import { AutomationStep } from '@/types/automationSteps.ts';
import { Run } from '@/types/runs.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import {
  RunStepDebugDatasetPreview,
  RunStepDebugPreview,
  RunStepDebugStepRunPreview,
  RunStepIterateChildDebug,
} from '@/types/runDebug.ts';
import { StepRun } from '@/types/stepExecutions.ts';

type Props = {
  opened: boolean;
  onClose: () => void;
  run: Run;
  step: AutomationStep;
  stepRun?: StepRun;
};

const StepDebugPreviewModal = ({ opened, onClose, run, step, stepRun }: Props) => {
  const { data, isPending, isError } = useRetrieveRunStepDebugPreview({
    runId: run.id,
    stepId: step.step_id,
    fullPath: stepRun?.full_path,
    stepRunId: stepRun?.id,
    enabled: opened,
  });

  return (
    <StyledModal
      opened={opened}
      onClose={onClose}
      title={`${step.name} - Debug Preview`}
      centered
      size="90vw"
      withCloseButton
      scrollType="none"
    >
      <ScrollArea h="calc(90vh - 120px)" scrollbars="y" offsetScrollbars="y">
        <Box px="xl" pt="lg" pb="4rem">
          {isPending && (
            <Group gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading debug preview
              </Text>
            </Group>
          )}

          {isError && (
            <Text size="sm" c="red">
              Unable to load debug preview.
            </Text>
          )}

          {data && <DebugPreviewContent data={data} requestedStepRunId={stepRun?.id} />}
        </Box>
      </ScrollArea>
    </StyledModal>
  );
};

const DebugPreviewContent = ({
  data,
  requestedStepRunId,
}: {
  data: RunStepDebugPreview;
  requestedStepRunId?: string;
}) => {
  const selectedStepRunId = requestedStepRunId || data.selected_step_run_id;
  if (data.step_runs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {data.selected_step_run_id
          ? 'No step run found for this exact ID.'
          : requestedStepRunId
            ? 'No step run returned for the requested ID; exact selection could not be verified.'
            : data.coverage === 'all'
              ? 'No historical step runs found for this step.'
              : 'No latest step runs found for this step.'}
      </Text>
    );
  }
  if (
    selectedStepRunId &&
    (data.step_runs.length !== 1 || data.step_runs[0].step_run_id !== selectedStepRunId)
  ) {
    return (
      <Text size="sm" c="red">
        Exact step run could not be verified; the server returned a different selection.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Badge variant="light" color="gray">
          {selectedStepRunId ? 'Exact step run' : `${data.coverage || 'latest'} coverage`}
        </Badge>
        {data.composition_ref?.scope_kind === 'local_subflow' && (
          <>
            <Badge variant="light" color="violet">
              Local module: {data.composition_ref.subflow_name || 'Unnamed module'}
            </Badge>
            <Badge variant="light" color="blue">
              Caller: {data.composition_ref.caller_name || data.composition_ref.caller_step_id || 'Unknown caller'}
            </Badge>
            {data.composition_ref.internal_step_name && (
              <Badge variant="light" color="gray">
                Internal: {data.composition_ref.internal_step_name}
              </Badge>
            )}
          </>
        )}
        <Badge variant="light" color="blue">
          {data.total_step_run_count} step run{data.total_step_run_count === 1 ? '' : 's'}
        </Badge>
        {data.full_path_filter && (
          <Badge variant="light" color="gray">
            Path filter: {data.full_path_filter}
          </Badge>
        )}
        {data.truncated_step_runs && (
          <Badge variant="light" color="yellow">
            Truncated
          </Badge>
        )}
      </Group>

      {data.step_runs.map((stepRunPreview) => (
        <StepRunDebugBlock key={stepRunPreview.step_run_id} stepRunPreview={stepRunPreview} />
      ))}
    </Stack>
  );
};

const StepRunDebugBlock = ({
  stepRunPreview,
}: {
  stepRunPreview: RunStepDebugStepRunPreview;
}) => {
  const attemptLabel = getAttemptLabel(stepRunPreview.attempt_number);
  const retryAttempt = stepRunPreview.retry_parent_attempt_number;

  return (
    <Box style={panelStyle}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" gap="sm">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text size="sm" fw={700} truncate>
              {stepRunPreview.full_path}
            </Text>
            <Text size="11px" c="dimmed">
              {attemptLabel ? `${attemptLabel} · ` : ''}
              Step run {stepRunPreview.step_run_id} · Execution {stepRunPreview.step_execution_id}
            </Text>
          </Stack>
          <Group gap={6}>
            {typeof retryAttempt === 'number' && (
              <Badge size="sm" variant="light" color="blue">
                Retry attempt {retryAttempt + 1}
              </Badge>
            )}
            {attemptLabel && (
              <Badge size="sm" variant="light" color="violet">
                {attemptLabel}
              </Badge>
            )}
            <Badge size="sm" variant="light" color={statusColor(stepRunPreview.status)}>
              {stepRunPreview.status}
            </Badge>
            <Badge
              size="sm"
              variant="light"
              color={stepRunPreview.used_existing_step_execution ? 'gray' : 'green'}
            >
              {stepRunPreview.used_existing_step_execution
                ? stepRunPreview.is_preserved_rerun_carrier
                  ? 'Preserved rerun work'
                  : 'Reused execution'
                : 'Fresh execution'}
            </Badge>
            {stepRunPreview.cache_decision && (
              <Badge
                size="sm"
                variant="light"
                color={cacheDecisionColor(stepRunPreview.cache_decision.outcome)}
                title={stepRunPreview.cache_decision.reason}
              >
                Candidate {cacheDecisionLabel(stepRunPreview.cache_decision.outcome)}
              </Badge>
            )}
            {stepRunPreview.code_execution_provenance && (
              <Badge
                size="sm"
                variant="light"
                color={stepRunPreview.code_execution_provenance.used_code_cache ? 'yellow' : 'blue'}
              >
                {codeSourceLabel(stepRunPreview.code_execution_provenance.effective_code_source)}
              </Badge>
            )}
          </Group>
        </Group>

        {stepRunPreview.code_execution_provenance && (
          <CodeProvenanceSection provenance={stepRunPreview.code_execution_provenance} />
        )}
        {stepRunPreview.review_lifecycle && (
          <ReviewLifecycleSection lifecycle={stepRunPreview.review_lifecycle} />
        )}

        <DatasetSection title="Inputs" items={stepRunPreview.inputs} emptyLabel="No inputs recorded." />
        <DatasetSection title="Outputs" items={stepRunPreview.outputs} emptyLabel="No public outputs recorded." />
        <DatasetSection
          title="Internal Evidence"
          items={stepRunPreview.internal_outputs}
          emptyLabel="No internal evidence datasets recorded."
        />
        <DatasetSection
          title="Log Datasets"
          items={stepRunPreview.log_datasets}
          emptyLabel="No log-linked datasets recorded."
        />

        {stepRunPreview.iterate_debug && <IterateDebugSection stepRunPreview={stepRunPreview} />}
      </Stack>
    </Box>
  );
};

const getAttemptLabel = (attemptNumber?: number | null) =>
  typeof attemptNumber === 'number' ? `Step attempt ${attemptNumber + 1}` : null;

const CodeProvenanceSection = ({
  provenance,
}: {
  provenance: NonNullable<RunStepDebugStepRunPreview['code_execution_provenance']>;
}) => (
  <Box style={nestedPanelStyle}>
    <Stack gap={5}>
      <Text size="11px" fw={700} c="dimmed" tt="uppercase">
        Code provenance
      </Text>
      <Text size="sm">
        Configured: {provenance.configured_code_source_mode} · Executed:{' '}
        {codeSourceLabel(provenance.effective_code_source)}
      </Text>
      {provenance.configured_provided_code_sha256 && provenance.effective_code_source !== 'provided' && (
        <Text size="12px" c="yellow.9">
          Provided code was retained but not executed.
        </Text>
      )}
      <Group gap="xs" wrap="wrap">
        {provenance.executed_code_sha256 && <Code>executed {shortHash(provenance.executed_code_sha256)}</Code>}
        {provenance.code_cache_contract_hash && <Code>contract {shortHash(provenance.code_cache_contract_hash)}</Code>}
        {provenance.configured_provided_code_sha256 && (
          <Code>provided {shortHash(provenance.configured_provided_code_sha256)}</Code>
        )}
      </Group>
    </Stack>
  </Box>
);

const codeSourceLabel = (source?: 'cached' | 'provided' | 'generated' | null) =>
  source
    ? ({ cached: 'Cached generated code', provided: 'Provided code', generated: 'Generated code' })[source]
    : 'Code selection failed';

const cacheDecisionLabel = (
  outcome: NonNullable<RunStepDebugStepRunPreview['cache_decision']>['outcome'],
) =>
  ({
    reused: 'reused',
    fresh: 'executed fresh',
    bypassed: 'cache bypassed',
    indeterminate: 'cache status unavailable',
  })[outcome];

const cacheDecisionColor = (
  outcome: NonNullable<RunStepDebugStepRunPreview['cache_decision']>['outcome'],
) => ({ reused: 'gray', fresh: 'green', bypassed: 'yellow', indeterminate: 'gray' })[outcome];

const ReviewLifecycleSection = ({
  lifecycle,
}: {
  lifecycle: NonNullable<RunStepDebugStepRunPreview['review_lifecycle']>;
}) => (
  <Box style={nestedPanelStyle}>
    <Stack gap={6}>
      <Text size="11px" fw={700} c="dimmed" tt="uppercase">
        Code execution and output review
      </Text>
      <Group gap={6} wrap="wrap">
        <Badge
          size="xs"
          variant="light"
          color={lifecycle.code_execution_status === 'succeeded' ? 'green' : 'gray'}
        >
          Code {lifecycle.code_execution_status === 'succeeded' ? 'succeeded' : 'not completed'}
        </Badge>
        <Badge size="xs" variant="light" color={reviewOutcomeColor(lifecycle.outcome)}>
          {reviewOutcomeLabel(lifecycle.outcome)}
        </Badge>
        <Badge size="xs" variant="light" color="gray">
          Review {reviewExecutionLabel(lifecycle.review_execution)}
        </Badge>
      </Group>
      <Text size="12px">
        Initial verdict: {lifecycle.initial_recommendation || 'not available'} · Final verdict:{' '}
        {lifecycle.final_recommendation || 'not verified'}
      </Text>
      {lifecycle.lifecycle_events.length > 1 && (
        <Text size="12px" c="dimmed">
          Review chain:{' '}
          {lifecycle.lifecycle_events
            .map((event) => `${event.phase || 'review'} ${event.status || 'recorded'}`)
            .join(' → ')}
        </Text>
      )}
      {lifecycle.inconsistent_verified_outcome && (
        <Text size="12px" c="red">
          Identical review inputs produced conflicting verified outcomes in this run.
        </Text>
      )}
      {lifecycle.comparison_truncated && (
        <Text size="12px" c="dimmed">
          Outcome comparison was limited to the 200 most recent executions.
        </Text>
      )}
      {lifecycle.review_surface_comparison_status === 'not_comparable' && (
        <Text size="12px" c="dimmed">
          Exact outcome comparison is unavailable because this review includes non-text evidence.
        </Text>
      )}
      {lifecycle.review_surface_fingerprint && (
        <Code>review surface {shortHash(lifecycle.review_surface_fingerprint)}</Code>
      )}
    </Stack>
  </Box>
);

const reviewOutcomeLabel = (
  outcome: NonNullable<RunStepDebugStepRunPreview['review_lifecycle']>['outcome'],
) =>
  ({
    accepted: 'Review accepted',
    rejected_verified: 'Review rejected with verified evidence',
    review_requires_user_input: 'Review requires user input',
    review_unverified: 'Review evidence unverified',
    review_unavailable: 'Review unavailable',
    review_not_run: 'Review not run',
  })[outcome];

const reviewOutcomeColor = (
  outcome: NonNullable<RunStepDebugStepRunPreview['review_lifecycle']>['outcome'],
) =>
  ({
    accepted: 'green',
    rejected_verified: 'red',
    review_requires_user_input: 'yellow',
    review_unverified: 'yellow',
    review_unavailable: 'yellow',
    review_not_run: 'gray',
  })[outcome];

const reviewExecutionLabel = (
  execution: NonNullable<RunStepDebugStepRunPreview['review_lifecycle']>['review_execution'],
) =>
  ({
    reused: 'reused',
    fresh: 'fresh',
    citation_repair: 'citation repair',
    not_run: 'not run',
  })[execution];

const shortHash = (value: string) => value.slice(0, 12);

const DatasetSection = ({
  title,
  items,
  emptyLabel,
}: {
  title: string;
  items: RunStepDebugDatasetPreview[];
  emptyLabel: string;
}) => (
  <Stack gap={6}>
    <Group gap={6}>
      <Text size="11px" fw={700} c="dimmed" tt="uppercase">
        {title}
      </Text>
      <Badge size="xs" variant="light" color={items.length > 0 ? 'blue' : 'gray'}>
        {items.length}
      </Badge>
    </Group>
    {items.length === 0 ? (
      <Text size="12px" c="dimmed">
        {emptyLabel}
      </Text>
    ) : (
      <Stack gap="xs">
        {items.map((item, index) => (
          <DatasetPreviewCard key={`${item.preview.dataset.id}-${item.source}-${item.output_id || index}`} item={item} />
        ))}
      </Stack>
    )}
  </Stack>
);

const DatasetPreviewCard = ({ item }: { item: RunStepDebugDatasetPreview }) => (
  <Box style={nestedPanelStyle}>
    <Stack gap={8}>
      <Group justify="space-between" gap="xs" align="center">
        <Group gap={6} wrap="wrap">
          <DatasetPill
            datasetId={item.preview.dataset.id}
            datasetName={item.preview.dataset.name}
            dataset={item.preview.dataset}
          />
          <Badge size="xs" variant="light" color={sourceColor(item.source)}>
            {sourceLabel(item)}
          </Badge>
          {item.preview.truncated && (
            <Badge size="xs" variant="light" color="yellow">
              Truncated
            </Badge>
          )}
        </Group>
        <Text size="11px" c="dimmed">
          {item.preview.preview_kind}
        </Text>
      </Group>
      {item.log_message && (
        <Text size="12px" c="dimmed" lineClamp={2}>
          {item.log_message}
        </Text>
      )}
      {item.preview.truncated && (
        <Text size="12px" c="dimmed">
          This preview omits content. Use the retained original for a complete audit.
          {(item.preview.truncation_details?.length ?? 0) > 0 &&
            ` Truncated paths: ${item.preview.truncation_details!.map((detail) => detail.path || '/').join(', ')}.`}
          {item.preview.truncation_details_truncated && ' Additional truncated paths are omitted.'}
        </Text>
      )}
      <DatasetPreviewBody item={item} />
    </Stack>
  </Box>
);

const DatasetPreviewBody = ({ item }: { item: RunStepDebugDatasetPreview }) => {
  const preview = item.preview;

  if (preview.error) {
    return (
      <Text size="12px" c="red">
        {preview.error}
      </Text>
    );
  }

  if (preview.child_datasets && preview.child_datasets.length > 0) {
    return (
      <Group gap={6} wrap="wrap">
        {preview.child_datasets.map((dataset) => (
          <DatasetPill key={dataset.id} datasetId={dataset.id} datasetName={dataset.name} dataset={dataset} />
        ))}
      </Group>
    );
  }

  if (preview.rows && preview.rows.length > 0) {
    return <RowsPreviewTable rows={preview.rows} columns={preview.columns} />;
  }

  if (preview.value !== null && preview.value !== undefined) {
    return (
      <Code block style={{ maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
        {formatValue(preview.value)}
      </Code>
    );
  }

  return (
    <Text size="12px" c="dimmed">
      No preview rows.
    </Text>
  );
};

const RowsPreviewTable = ({ rows, columns }: { rows: unknown[]; columns: string[] | null }) => {
  const visibleColumns = (columns && columns.length > 0 ? columns : inferColumns(rows)).slice(0, 6);

  if (visibleColumns.length === 0) {
    return (
      <Code block style={{ maxHeight: 180, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
        {formatValue(rows)}
      </Code>
    );
  }

  return (
    <ScrollArea type="auto">
      <Table withTableBorder withColumnBorders verticalSpacing={4} horizontalSpacing={6} fz="xs">
        <Table.Thead>
          <Table.Tr>
            {visibleColumns.map((column) => (
              <Table.Th key={column}>{column}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, rowIndex) => (
            <Table.Tr key={rowIndex}>
              {visibleColumns.map((column) => (
                <Table.Td key={column} maw={260}>
                  <Text size="11px" lineClamp={3}>
                    {formatCell(row, column)}
                  </Text>
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea>
  );
};

const IterateDebugSection = ({
  stepRunPreview,
}: {
  stepRunPreview: RunStepDebugStepRunPreview;
}) => {
  const iterateDebug = stepRunPreview.iterate_debug;
  if (!iterateDebug) return null;

  return (
    <Stack gap="xs">
      <Divider />
      <Group gap={6}>
        <Text size="11px" fw={700} c="dimmed" tt="uppercase">
          Iterations
        </Text>
        <Badge size="xs" variant="light" color="blue">
          {iterateDebug.child_count}
        </Badge>
        {iterateDebug.truncated_children && (
          <Badge size="xs" variant="light" color="yellow">
            Truncated
          </Badge>
        )}
      </Group>
      {iterateDebug.parent_children_output && (
        <DatasetPreviewCard item={iterateDebug.parent_children_output} />
      )}
      {iterateDebug.children.length > 0 ? (
        <Accordion variant="separated" multiple>
          {iterateDebug.children.map((child) => (
            <Accordion.Item key={child.child_index} value={String(child.child_index)}>
              <Accordion.Control>
                <IterationHeader child={child} />
              </Accordion.Control>
              <Accordion.Panel>
                <IterationBody child={child} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      ) : (
        <RunInlineEmptyState message="No child iterations" />
      )}
    </Stack>
  );
};

const IterationHeader = ({ child }: { child: RunStepIterateChildDebug }) => (
  <Group gap={6} wrap="wrap">
    <Text size="sm" fw={600}>
      Child {child.child_index}
    </Text>
    {child.iteration_key && (
      <Badge size="sm" variant="light" color="gray">
        {child.iteration_key}
      </Badge>
    )}
    {child.status && (
      <Badge size="sm" variant="light" color={statusColor(child.status)}>
        {child.status}
      </Badge>
    )}
    <Badge size="sm" variant="light" color="green">
      {child.fresh_step_count} fresh
    </Badge>
    <Badge size="sm" variant="light" color="gray">
      {child.cached_step_count} cached
    </Badge>
  </Group>
);

const IterationBody = ({ child }: { child: RunStepIterateChildDebug }) => (
  <Stack gap="sm">
    {child.current_item && (
      <DatasetSection title="Current Item" items={[child.current_item]} emptyLabel="No current item." />
    )}
    {child.filter_selections.length > 0 && (
      <Stack gap={6}>
        <Text size="11px" fw={700} c="dimmed" tt="uppercase">
          Filter Selection
        </Text>
        {child.filter_selections.map((selection) => (
          <Box key={`${selection.step_run_id}-${selection.output_id}`} style={nestedPanelStyle}>
            <Stack gap={6}>
              <Group gap={6} wrap="wrap">
                <Badge size="xs" variant="light" color="blue">
                  {selection.selection_kind || 'selection'}
                </Badge>
                <Badge size="xs" variant="light" color="gray">
                  {selection.selected_count} selected
                </Badge>
                {selection.selected_keys.map((key) => (
                  <Badge key={key} size="xs" variant="outline" color="blue">
                    {key}
                  </Badge>
                ))}
                {selection.selected_keys_truncated && (
                  <Badge size="xs" variant="light" color="yellow">
                    More selected keys
                  </Badge>
                )}
              </Group>
              <DatasetPreviewCard item={selection.decision_dataset} />
            </Stack>
          </Box>
        ))}
      </Stack>
    )}
    {child.child_step_runs.map((childStepRun) => (
      <StepRunDebugBlock key={childStepRun.step_run_id} stepRunPreview={childStepRun} />
    ))}
  </Stack>
);

const inferColumns = (rows: unknown[]): string[] => {
  const columns = new Set<string>();
  rows.forEach((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return;
    Object.keys(row).forEach((key) => {
      columns.add(key);
    });
  });
  return Array.from(columns);
};

const formatCell = (row: unknown, column: string): string => {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return formatValue(row);
  return formatValue((row as Record<string, unknown>)[column]);
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const sourceLabel = (item: RunStepDebugDatasetPreview): string => {
  if (item.output_id) return `${sourceBaseLabel(item.source)} · ${item.output_id}`;
  return sourceBaseLabel(item.source);
};

const sourceBaseLabel = (source: RunStepDebugDatasetPreview['source']): string => {
  if (source === 'internal_output') return 'Internal';
  if (source === 'log_dataset') return 'Log dataset';
  if (source === 'current_item') return 'Current item';
  if (source === 'output') return 'Output';
  return 'Input';
};

const sourceColor = (source: RunStepDebugDatasetPreview['source']): string => {
  if (source === 'internal_output') return 'violet';
  if (source === 'log_dataset') return 'orange';
  if (source === 'current_item') return 'cyan';
  if (source === 'output') return 'green';
  return 'blue';
};

const statusColor = (status: string): string => {
  if (status === ExecutionStatus.Completed) return 'green';
  if (status === ExecutionStatus.PartiallyCompleted || status === ExecutionStatus.ActiveWithErrors) return 'yellow';
  if (status === ExecutionStatus.Errors || status === ExecutionStatus.Cancelled) return 'red';
  if (
    status === ExecutionStatus.Active ||
    status === ExecutionStatus.Preprocessing ||
    status === ExecutionStatus.RerunPreparing ||
    status === ExecutionStatus.Rerunning
  ) {
    return 'blue';
  }
  return 'gray';
};

const panelStyle = {
  border: '1px solid #ECECED',
  borderRadius: 6,
  padding: 12,
  background: '#FFFFFF',
};

const nestedPanelStyle = {
  border: '1px solid #ECECED',
  borderRadius: 6,
  padding: 10,
  background: '#FDFDFD',
};

export default StepDebugPreviewModal;
